import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect, useSelector } from 'react-redux';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Portal from '@mui/material/Portal';
import { Link } from 'react-router-dom';
import ListItemIcon from '@mui/material/ListItemIcon';
import { useNavigate } from 'react-router-dom';
import useStyles from './../useStyles';
import translationService from '../services/translator.service';
import { bindActionCreators } from 'redux';
import userInfoActions from '../store/actions/userInfoActions';

import { userTypeEnum } from '../enums/auth/userType.enum';
import { userPermissionsEnum } from '../enums/auth/userPermissions.enum';
import { Box } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useTheme } from '@mui/styles';
import MenuToggleIcon from '../components/shared/common/icons/MenuToggleIcon';
import BackChevronIcon from '../components/shared/common/icons/BackChevronIcon';
import AppMenuMyChargePointsIcon from '../components/shared/common/icons/AppMenuMyChargePointsIcon';
import {
    AppMenuRfidIcon,
    AppMenuDriversIcon,
    AppMenuLocationsIcon,
    AppMenuChargerGroupsIcon,
    AppMenuLoadManagementGroupsIcon,
    AppMenuStrategiesIcon,
    AppMenuChargingHistoryIcon,
    AppMenuPaymentMethodsIcon,
    AppMenuPersonalDetailsIcon,
    AppMenuSettingsIcon,
    AppMenuLogoutIcon,
} from '../components/shared/common/icons/AppMenuIcons';

const APP_BAR_HEIGHT = 64;
const HEADER_DIVIDER_THICKNESS = 3;
const BOTTOM_NAV_CONTENT_HEIGHT = 56;
const BOTTOM_NAV_TOP_SPACING = 8;

const useDrawerStyles = makeStyles((theme) => ({
    surface: {
        position: 'fixed',
        width: '100vw',
        maxWidth: '100vw',
        left: 0,
        right: 0,
        top: APP_BAR_HEIGHT + HEADER_DIVIDER_THICKNESS,
        bottom: BOTTOM_NAV_CONTENT_HEIGHT + BOTTOM_NAV_TOP_SPACING + 2,
        height: 'auto',
        backgroundColor: theme.palette.background.default,
        boxSizing: 'border-box',
        overflowY: 'auto',
        boxShadow: 'none',
        borderRadius: 0,
        zIndex: theme.zIndex.modal + 1,
    },
    menuList: {
        width: '100%',
    },
    menuText: {
        color: theme.palette.text.primary,
        paddingLeft: theme.spacing(5),
        paddingRight: theme.spacing(5),
        '& .MuiListItemIcon-root': {
            minWidth: theme.spacing(4),
            marginRight: theme.spacing(0.5),
        },
    },
    backMenuItem: {
        fontWeight: 600,
    },
}));

const UserMenu = ({ userInfoReducer, setScreenItem, languageReducer, userInfoActions, chargerReducer, onMenuStateChange, ...props }) => {
    const classes = useStyles();
    const theme = useTheme();
    const drawerClasses = useDrawerStyles();
    const menuIconOptions = {
        size: 22,
        primaryColor: theme.palette.text.primary,
        strokeColor: theme.palette.text.primary,
        mutedColor: theme.palette.text.primary,
    };
    let navigate = useNavigate();

    const { userInfo } = useSelector((state) => state.userInfoReducer);

    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [hasPayment, setHasPayment] = useState(null);
    const [translatedData, setTranslatedData] = useState();
    const [disableMenuByUserType, setDisableMenuByUserType] = useState(false);

    useEffect(() => {
        setHasPayment(userInfoReducer.userPaymentInfo && Object.keys(userInfoReducer.userPaymentInfo).length > 0 ? true : false);
        setTranslatedData(translationService.getTranslatedData(languageReducer.language, 'userMenuComponent'));

        checkDisableMenuByUserType();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userInfoReducer, hasPayment, languageReducer.language]);

    useEffect(() => {
        const closeMenuOnBottomTabClick = () => {
            setIsMenuOpen(false);
        };

        window.addEventListener('mobile-user-menu-close', closeMenuOnBottomTabClick);

        return () => {
            window.removeEventListener('mobile-user-menu-close', closeMenuOnBottomTabClick);
        };
    }, []);

    useEffect(() => {
        if (onMenuStateChange) {
            onMenuStateChange(isMenuOpen);
        }
    }, [isMenuOpen, onMenuStateChange]);

    const handleMenuClose = () => {
        setIsMenuOpen(false);
    };

    const handleMenuClick = (value) => {
        const parsedValue = parseInt(value, 10);

        if (!Number.isNaN(parsedValue)) {
            setScreenItem(parsedValue);
        }

        setIsMenuOpen(false);
    };

    const handleLogOut = () => {
        setIsMenuOpen(false);
        localStorage.clear();
        sessionStorage.clear();

        userInfoActions.logout();
        navigate('/');
    };

    const handleProfileMenuOpen = (event) => {
        event.preventDefault();

        if (isMenuOpen) {
            setIsMenuOpen(false);
            return;
        }

        setIsMenuOpen(true);
    };

    const checkDisableMenuByUserType = () => {
        let disabled = true;

        const { userType } = userInfoReducer?.userInfo;
        if (userType === 'Administrator' || userType === 'Company' || userType === 'Operation_Manager' || userType === 'Public_OPU') {
            disabled = false;
        }
        setDisableMenuByUserType(disabled);
    };

    const hideFromOperationManagerWithViewPermissions = () => {
        if (userInfo && userInfo.userType === userTypeEnum.OperationManager && userInfo.permissions === userPermissionsEnum.View) return true;
        return false;
    };

    const getMenuIconProps = (overrides = {}) => ({
        size: menuIconOptions.size,
        primaryColor: menuIconOptions.primaryColor,
        strokeColor: menuIconOptions.strokeColor,
        ...overrides,
    });

    const menuId = 'primary-search-account-menu';
    const renderMenu = (
        isMenuOpen && (
            <Portal>
                <Box id={menuId} role='menu' className={drawerClasses.surface}>
                    {!hideFromOperationManagerWithViewPermissions() && <Box className={drawerClasses.menuList}>
                        {/* המטענים שלי */}
                        <MenuItem name={1} component={Link} to='/OperatorChargePoints' onClick={() => handleMenuClick(1)} className={drawerClasses.menuText}>
                            <ListItemIcon>
                                <AppMenuMyChargePointsIcon {...getMenuIconProps()} />
                            </ListItemIcon>
                            {userInfoReducer.userInfo.isOPU ? translationService.getTranslation(translatedData, 'general.titles.myChargePointsTitle') : translationService.getTranslation(translatedData, 'general.titles.myChargePointsTitle')}
                        </MenuItem>

                {/* כרטיסי RFID */}
                <MenuItem name={1} component={Link} to='/RFIDCards' onClick={() => handleMenuClick(1)} className={drawerClasses.menuText}>
                    <ListItemIcon>
                        <AppMenuRfidIcon {...getMenuIconProps()} />
                    </ListItemIcon>
                    {userInfoReducer.userInfo.isOPU ? translationService.getTranslation(translatedData, 'general.titles.rfidCardsTitle') : translationService.getTranslation(translatedData, 'general.titles.rfidCardsTitle')}
                </MenuItem>

                {/* נהגים */}
                {userInfoReducer.userInfo.isOPU && (
                    <MenuItem name={1} component={Link} to='/Driver' onClick={() => handleMenuClick(1)} className={drawerClasses.menuText}>
                        <ListItemIcon>
                            <AppMenuDriversIcon {...getMenuIconProps()} />
                        </ListItemIcon>
                        {userInfoReducer.userInfo.isOPU ? translationService.getTranslation(translatedData, 'general.titles.driversTitle') : translationService.getTranslation(translatedData, 'general.titles.driversTitle')}
                    </MenuItem>
                )}

                {/* מיקומים */}
                {!disableMenuByUserType && (
                    <MenuItem name={1} component={Link} to='/locations' onClick={() => handleMenuClick(1)} className={drawerClasses.menuText}>
                        <ListItemIcon>
                            <AppMenuLocationsIcon {...getMenuIconProps()} />
                        </ListItemIcon>
                        {translationService.getTranslation(translatedData, 'locations')}
                    </MenuItem>
                )}

                {/* קבוצות מטענים */}
                {userInfoReducer.userInfo.isOPU ? (
                    <MenuItem name={2} component={Link} to='/business-group' onClick={() => handleMenuClick(2)} className={drawerClasses.menuText}>
                        <ListItemIcon>
                            <AppMenuChargerGroupsIcon {...getMenuIconProps()} />
                        </ListItemIcon>
                        {translationService.getTranslation(translatedData, 'chargerGroups')}
                    </MenuItem>
                ) : (
                    ''
                )}

                {userInfoReducer.userInfo.isOPU ? (
                    <MenuItem name={2} component={Link} to='/myGroups' onClick={() => handleMenuClick(2)} className={drawerClasses.menuText}>
                        <ListItemIcon>
                            <AppMenuLoadManagementGroupsIcon {...getMenuIconProps()} />
                        </ListItemIcon>
                        {translationService.getTranslation(translatedData, 'powerSourceGroups')}
                    </MenuItem>
                ) : (
                    ''
                )}

                {/* אסטרטגיות טעינה */}
                {!disableMenuByUserType && (
                    <MenuItem name={1} component={Link} to='/strategy' onClick={() => handleMenuClick(1)} className={drawerClasses.menuText}>
                        <ListItemIcon>
                            <AppMenuStrategiesIcon {...getMenuIconProps()} />
                        </ListItemIcon>
                        {translationService.getTranslation(translatedData, 'chargingStrategy')}
                    </MenuItem>
                )}

                {/* אמצעי תשלום */}
                <MenuItem name={1} component={Link} to='/updateBilling' onClick={() => handleMenuClick(1)} className={drawerClasses.menuText}>
                    <ListItemIcon>
                        <AppMenuPaymentMethodsIcon {...getMenuIconProps()} />
                    </ListItemIcon>
                    {translationService.getTranslation(translatedData, 'paymentMethod')}
                </MenuItem>

                {/* היסטוריית טעינה */}
                <MenuItem name={3} component={Link} to='/payments' onClick={() => handleMenuClick(3)} className={drawerClasses.menuText}>
                    <ListItemIcon>
                        <AppMenuChargingHistoryIcon {...getMenuIconProps()} />
                    </ListItemIcon>
                    {translationService.getTranslation(translatedData, 'chargingHistory')}
                </MenuItem>

                {/* פרטים אישיים */}
                <MenuItem name={4} component={Link} to='/personal' onClick={() => handleMenuClick(4)} className={drawerClasses.menuText}>
                    <ListItemIcon>
                        <AppMenuPersonalDetailsIcon {...getMenuIconProps({ primaryColor: menuIconOptions.mutedColor })} />
                    </ListItemIcon>
                    {translationService.getTranslation(translatedData, 'personalDetails')}
                </MenuItem>
                        {/* הגדרות */}
                        <MenuItem name={100} component={Link} to='/settings' onClick={() => handleMenuClick(100)} className={drawerClasses.menuText}>
                            <ListItemIcon>
                                <AppMenuSettingsIcon {...getMenuIconProps()} />
                            </ListItemIcon>
                            {translationService.getTranslation(translatedData, 'settings')}
                        </MenuItem>
                    </Box>}
                    {/* התנתק */}
                    <MenuItem name={5} onClick={handleLogOut} className={drawerClasses.menuText}>
                        <ListItemIcon>
                            <AppMenuLogoutIcon {...getMenuIconProps()} />
                        </ListItemIcon>
                        {translationService.getTranslation(translatedData, 'logout')}
                    </MenuItem>
                    <MenuItem onClick={handleMenuClose} className={drawerClasses.menuText}>
                        <ListItemIcon>
                            <BackChevronIcon />
                        </ListItemIcon>
                        {translationService.getTranslation(translatedData, 'back')}
                    </MenuItem>
                </Box>
            </Portal>
        )
    );

    return (
        <div>
            <MenuItem onClick={handleProfileMenuOpen} className={classes.root}>
                <IconButton aria-label={translationService.getTranslation(translatedData, 'general.a11y.toggleUserMenu')} aria-controls='primary-search-account-menu' aria-haspopup='true' color='inherit'>
                    <MenuToggleIcon isOpen={isMenuOpen} />
                </IconButton>
            </MenuItem>
            {renderMenu}
        </div>
    );
};

UserMenu.propTypes = {
    userInfoReducer: PropTypes.object.isRequired,
    onMenuStateChange: PropTypes.func,
};

const mapStateToProps = (state) => ({
    chargerReducer: state.chargerReducer,
    userInfoReducer: state.userInfoReducer,
    languageReducer: state.languageReducer,
});

const mapDispatchToProps = (dispatch) => {
    return {
        userInfoActions: bindActionCreators(userInfoActions, dispatch),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(UserMenu);
